import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Calendar, Users, Target, BarChart } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export const ProjectLink = () => {
  const features = [
    {
      icon: <Target className="w-10 h-10 text-[#00890b]" />,
      title: 'Project Planning',
      description: 'Comprehensive project planning with deliverable tracking and milestones',
    },
    {
      icon: <Users className="w-10 h-10 text-[#00890b]" />,
      title: 'Resource Management',
      description: 'Optimize resource allocation and team collaboration',
    },
    {
      icon: <Calendar className="w-10 h-10 text-[#00890b]" />,
      title: 'Schedule Management',
      description: 'Advanced scheduling with critical path analysis and dependencies',
    },
    {
      icon: <BarChart className="w-10 h-10 text-[#00890b]" />,
      title: 'Progress Tracking',
      description: 'Real-time project metrics and performance analytics',
    },
  ];

  const capabilities = [
    'Project Structure Definition',
    'Work Package Management',
    'Deliverable Tracking',
    'Milestone Management',
    'Resource Allocation',
    'Gantt Chart Visualization',
    'Critical Path Analysis',
    'Baseline Comparison',
    'Risk Management',
    'Issue Tracking',
    'Project Reports & Dashboards',
    'Cross-Project Analysis',
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hire Windchill ProjectLink Expert | Outsource ProjectLink Customization"
        description="Hire experienced Windchill ProjectLink developers. Outsource your ProjectLink customization to expert consultants for engineering project management solutions."
        keywords="hire windchill projectlink expert, outsource projectlink customization, hire projectlink developer, windchill project management expert, hire projectlink consultant"
        canonical="/hire-projectlink-customization-expert"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#00890b] hover:bg-[#006a08] text-white px-4 py-2">
              Project Management Excellence
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hire Windchill ProjectLink Customization Expert
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Outsource your ProjectLink development to experienced consultants. Hire expert
              Windchill ProjectLink developers for engineering project management
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Windchill ProjectLink Overview</h2>
          <p className="text-lg text-gray-600 mb-4">
            Windchill ProjectLink provides powerful project management capabilities specifically
            designed for engineering and product development organizations. It enables seamless
            integration between project schedules and product data, ensuring alignment across
            teams and processes.
          </p>
          <p className="text-lg text-gray-600">
            Our ProjectLink expertise helps organizations optimize project execution, improve
            resource utilization, and gain real-time visibility into project status and performance.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ProjectLink Capabilities
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors"
              >
                <CheckCircle className="w-6 h-6 text-[#00890b] flex-shrink-0" />
                <span className="text-gray-700 font-medium">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Expert Windchill ProjectLink Development
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Looking for experienced Windchill ProjectLink developers? Our team specializes in
            ProjectLink customization, including custom reports, workflow integration, and
            third-party system connections. We help organizations maximize their ProjectLink
            investment through tailored solutions.
          </p>
          <p className="text-lg text-gray-600">
            From initial ProjectLink implementation to ongoing enhancements and upgrades, we provide
            comprehensive support. Our developers understand the intricacies of project-product
            integration and deliver solutions that improve project visibility and team collaboration.
          </p>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};